import { useRef } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { ModalButtonWithForm } from 'components/common';
import {
  FormGroup,
  MultipleAttachments,
  TextArea
} from 'components/common/form';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';

import type { Attachments } from 'shared-types';
import type { GetAchievementGroupCompletionsRes } from 'shared-types/responses';

export type FormValues = {
  [key: `userAttributes-${string}`]: string | number | boolean;
  comments: string;
};

interface Props {
  groupCompletion: GetAchievementGroupCompletionsRes['groupCompletions'][number];
  updateGroups: () => Promise<void>;
}

const AchievementGroupOfUsersUpdate = ({
  groupCompletion,
  updateGroups
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { achievement }
  } = useReduxState();

  const uploader = useRef<() => Promise<Attachments>>(undefined);

  const submit = async (values: FormValues) => {
    try {
      let attachments = [];
      if (groupCompletion.achievementData.canSeeAttachments) {
        attachments = await uploader.current();
      }

      const groupAttributes = {};
      Object.keys(values).forEach((fieldKey) => {
        if (fieldKey.startsWith('userAttributes-')) {
          const attributeId = fieldKey.split('-')[1];
          groupAttributes[attributeId] = values[fieldKey];
        }
      });

      await api.put(
        `/admin/achievements/${achievement.id}/group-completions/${groupCompletion.achievementData.id}`,
        {
          updatedCompletion: {
            comments: values.comments ?? null,
            userAttributes: groupAttributes,
            ...(groupCompletion.achievementData.canSeeAttachments
              ? { attachments }
              : {})
          }
        }
      );

      await updateGroups();

      dispatch.addNotice({
        text: messages.achievementGroupConversationUpdateSuccess,
        type: 'success'
      });
    } catch (err) {
      if (err.message === messages.commonUploadFileFailed) {
        return false; // keep the modal open, user already notified by the uploader
      } else {
        await dispatch.handleErrorDispatch(err);
      }
    }
  };

  const initialValues = {
    attachments: groupCompletion.achievementData.attachments,
    comments: groupCompletion.achievementData.comments,
    ...Object.keys(groupCompletion.achievementData.userAttributes || {}).reduce(
      (fields, attrId) => {
        fields[`userAttributes-${attrId}`] =
          groupCompletion.achievementData.userAttributes?.[attrId];
        return fields;
      },
      {}
    )
  };

  return (
    <ModalButtonWithForm
      buttonCategory="lighter"
      buttonIcon="pen"
      buttonIsDropdown={true}
      buttonText={messages.commonUpdateDetails}
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonSave}
      modalAutoFocus={false}
      modalTitle={messages.achievementCompletionDetails}
      modalWidth={'medium'}
      onSubmit={submit}
    >
      {() => (
        <div className="header">
          <FormGroup label={messages.commonComment} maxWidth="100%">
            <TextArea
              autoGrow={true}
              field="comments"
              id={`achievement-comments-${
                groupCompletion.achievementData.id || 0
              }`}
              rows={4}
            />
          </FormGroup>

          {groupCompletion.achievementData.canSeeAttachments ? (
            <MultipleAttachments
              getUploadHandler={(fn) => (uploader.current = fn)}
            />
          ) : null}

          <div className="user-attributes">
            {achievement.userAttributes?.map((attribute) => (
              <FormGroup
                key={attribute.id}
                label={
                  attribute.titleTranslations?.[language] || attribute.title
                }
                labelIsOptional={!attribute.isRequired}
                style={{ marginTop: 20, maxWidth: 250 }}
              >
                <ProductUserCustomAttributeRenderFormField
                  attribute={attribute}
                />
              </FormGroup>
            ))}
          </div>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default AchievementGroupOfUsersUpdate;
